import apiFetch from '@/utils/apiFetch';
import { defineStore } from 'pinia';
import DefaultConfig from '@/config/default';
import type { ApiKey, APIRequest } from '@/config/types/responses';
import type { Pagination } from '@/config/types/pagination';

// eslint-disable-next-line import/prefer-default-export
export const useAPIKeysStore = defineStore('apiKeys', {
  state: () => ({
    loading: false,
    error: null as string | null,
    apiKeys: [] as ApiKey[],
    pagination: {} as Pagination,
    apiKey: null as string | null,
    apiSecret: null as string | null,
    activekeysCount: 0,
    monthlyUsageCount: 0,
    apiRequests: [] as APIRequest[],
  }),

  actions: {
    async getAPIKeys(page: number) {
      this.error = null;
      this.loading = true;

      const defaultConfig = new DefaultConfig();
      const token = localStorage.getItem('token');
      try {
        const res = await apiFetch(`${defaultConfig.baseURL}/api/auth/public/keys?page=${page}`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        });
        if (!res.ok) {
          throw new Error('Error ');
        }
        const data = await res.json();
        this.apiKeys = data.data.results;
        this.pagination = data.data.pagination;
      } catch (err: any) {
        this.error = err.message || 'Something went wrong';
      } finally {
        this.loading = false;
      }
    },
    async getAdminAPIKeys(page: number) {
      this.error = null;
      this.loading = true;

      const defaultConfig = new DefaultConfig();
      const token = localStorage.getItem('token');
      try {
        const res = await apiFetch(`${defaultConfig.baseURL}/api/auth/admin/keys?page=${page}`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
        });
        if (!res.ok) {
          throw new Error('Error ');
        }
        const data = await res.json();
        this.apiKeys = data.data.results;
        this.pagination = data.data.pagination;
      } catch (err: any) {
        this.error = err.message || 'Something went wrong';
      } finally {
        this.loading = false;
      }
    },
    async generateAPIKeys(environment: string) {
      this.error = null;
      this.loading = true;

      const defaultConfig = new DefaultConfig();
      const token = localStorage.getItem('token');
      try {
        const res = await apiFetch(`${defaultConfig.baseURL}/api/auth/public/keys`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ environment }),
        });
        if (!res.ok) {
          throw new Error('Error generating api keys');
        }
        const data = await res.json();
        this.apiKey = data.data.apiKey;
        this.apiSecret = data.data.apiSecret;
      } catch (err: any) {
        this.error = err.message || 'Something went wrong';
      } finally {
        this.loading = false;
      }
    },

    async getAPIKeyStats() {
      this.error = null;
      this.loading = true;

      const defaultConfig = new DefaultConfig();
      const token = localStorage.getItem('token');
      try {
        const res = await apiFetch(`${defaultConfig.baseURL}/api/auth/public/keys/stats`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        });
        if (!res.ok) {
          throw new Error('Error getting api key stats');
        }
        const data = await res.json();
        this.activekeysCount = data.data.activeKeysCount;
        this.monthlyUsageCount = data.data.monthlyUsageCount;
      } catch (err: any) {
        this.error = err.message || 'Something went wrong';
      } finally {
        this.loading = false;
      }
    },


    async getAdminAPIKeyStats() {
      this.error = null;
      this.loading = true;

      const defaultConfig = new DefaultConfig();
      const token = localStorage.getItem('token');
      try {
        const res = await apiFetch(`${defaultConfig.baseURL}/api/auth/admin/keys/stats`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        });
        if (!res.ok) {
          throw new Error('Error getting api key stats');
        }
        const data = await res.json();
        this.activekeysCount = data.data.activeKeysCount;
        this.monthlyUsageCount = data.data.monthlyUsageCount;
      } catch (err: any) {
        this.error = err.message || 'Something went wrong';
      } finally {
        this.loading = false;
      }
    },

    //nxz5YK8aPtPL1jiyu0hjE

    async getAPIRequests(page: number, pageSize = 10) {
      this.error = null;
      this.loading = true;

      const defaultConfig = new DefaultConfig();
      const token = localStorage.getItem('token');
      try {
        const res = await apiFetch(`${defaultConfig.baseURL}/api/auth/public/keys/requests?page=${page}&pageSize=${pageSize}`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        });
        if (!res.ok) {
          throw new Error('Error getting api requests');
        }
        const data = await res.json();
        this.apiRequests = data.data.results ?? [];
        this.pagination = data.data.pagination ?? {};
      } catch (err: any) {
        this.error = err.message || 'Something went wrong';
      } finally {
        this.loading = false;
      }
    },

    async getAdminAPIRequests(page: number, pageSize = 10) {
      this.error = null;
      this.loading = true;

      const defaultConfig = new DefaultConfig();
      const token = localStorage.getItem('token');
      try {
        const res = await apiFetch(`${defaultConfig.baseURL}/api/auth/admin/keys/requests?page=${page}&pageSize=${pageSize}`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        });
        if (!res.ok) {
          throw new Error('Error getting api requests');
        }
        const data = await res.json();
        this.apiRequests = data.data.results ?? [];
        this.pagination = data.data.pagination ?? {};
      } catch (err: any) {
        this.error = err.message || 'Something went wrong';
      } finally {
        this.loading = false;
      }
    },

    async updateAPIKeyStatus(id: string, status: string) {
      this.error = null;
      this.loading = true;

      const defaultConfig = new DefaultConfig();
      const token = localStorage.getItem('token');
      try {
        const res = await apiFetch(`${defaultConfig.baseURL}/api/auth/public/keys/status`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ status, id }),
        });
        if (!res.ok) {
          throw new Error('Error updating API key status');
        }
      } catch (err: any) {
        this.error = err.message || 'Something went wrong';
      } finally {
        this.loading = false;
      }
    },

    async deleteAPIKey(keyId: string) {
      this.error = null;
      this.loading = true;

      const defaultConfig = new DefaultConfig();
      const token = localStorage.getItem('token');
      try {
        const res = await apiFetch(`${defaultConfig.baseURL}/api/auth/public/keys/${keyId}`, {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        });
        if (!res.ok) {
          throw new Error('Error deleting API key');
        }
      } catch (err: any) {
        this.error = err.message || 'Something went wrong';
      } finally {
        this.loading = false;
      }
    },
  },
});
